import { FC, useEffect } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Switch,
	TextField,
	Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
	resetPgSaveSuccess,
	updatePgAirwallexAccountRequest,
} from 'redux/reducers/pgSettings/reducer';
import { selectPgSaveLoading, selectPgSaveSuccess } from 'redux/reducers/pgSettings/selectors';
import { TPgAirwallexAccount } from 'services/api/paymentGateway/types';

type TAirwallexAccountDialogProps = {
	open: boolean;
	onClose: () => void;
	account: TPgAirwallexAccount | null;
};

const validationSchema = Yup.object({
	displayName: Yup.string()
		.trim()
		.required('Display name is required')
		.max(200, 'Max 200 characters'),
});

/**
 * Edit an Airwallex account (03 §4.4): only the display name and the flags. Accounts themselves and
 * their API keys come from the gateway configuration (`Airwallex:Accounts:<code>`).
 */
export const AirwallexAccountDialog: FC<TAirwallexAccountDialogProps> = ({
	open,
	onClose,
	account,
}) => {
	const dispatch = useDispatch();
	const loading = useSelector(selectPgSaveLoading('airwallexAccount'));
	const success = useSelector(selectPgSaveSuccess('airwallexAccount'));

	const formik = useFormik({
		initialValues: {
			displayName: account?.displayName ?? '',
			isActive: account?.isActive ?? true,
			isDefault: account?.isDefault ?? false,
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			if (!account) return;
			dispatch(
				updatePgAirwallexAccountRequest({
					id: account.id,
					body: {
						displayName: values.displayName.trim(),
						isActive: values.isActive,
						isDefault: values.isDefault,
					},
				}),
			);
		},
	});

	useEffect(() => {
		if (!success) return;
		dispatch(resetPgSaveSuccess('airwallexAccount'));
		formik.resetForm();
		onClose();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [success]);

	const handleClose = () => {
		if (loading) return;
		formik.resetForm();
		onClose();
	};

	const defaultLocked = !!account?.isDefault;
	const activationBlocked = !!account && !account.isActive && !account.credentialsConfigured;

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle color="primary">Edit Airwallex account {account?.code}</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<TextField
						label="Display name"
						name="displayName"
						value={formik.values.displayName}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.displayName && !!formik.errors.displayName}
						helperText={formik.touched.displayName && formik.errors.displayName}
						size="small"
					/>
					<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
						<FormControlLabel
							control={
								<Switch
									checked={formik.values.isActive}
									disabled={activationBlocked}
									onChange={(e) => {
										void formik.setFieldValue('isActive', e.target.checked);
									}}
								/>
							}
							label="Active"
						/>
						<FormControlLabel
							control={
								<Switch
									checked={formik.values.isDefault}
									disabled={defaultLocked}
									onChange={(e) => {
										void formik.setFieldValue('isDefault', e.target.checked);
									}}
								/>
							}
							label="Default"
						/>
					</Box>
					{activationBlocked && (
						<Typography variant="caption" color="error">
							The account cannot be activated: its API key is not configured in the gateway
							(Airwallex:Accounts:{account?.code}).
						</Typography>
					)}
					{defaultLocked && (
						<Typography variant="caption" color="text.secondary">
							To change the default account, make another one default.
						</Typography>
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading || !account}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};
